import Template from './Template';
import { isAttributeChangeObservable } from '../model/Model';
import { setup } from '../setup/Setup';
import { renderer } from './Renderer';
import { Subscription } from '../signal/Signal';


/**
 * This is one of the three [[ViewRegistration]] forms. It's the simplest form when you already have
 * a view class whose constructor does not take any parameters.
 *
 * @example
 *
 *```typescript
 * import register from '.../core/setup/register';
 * class AppView extends View {
 *     constructor() { ... }
 *     ...
 * }
 * register(({ view }) => view('App', AppView));
 * //                                 ^^^^^^^
 *```
 */
export interface ViewConstructor {
	new( config:ViewConfig ):View;
}


/**
 * This is one of the three [[ViewRegistration]] forms. This allows you to specify some common aspects
 * of a `View` class (such as its HTML `template`) without actually writing one. This is mostly useful
 * for `View`s that only need a template but no further logic.
 *
 * @example
 *
 *```typescript
 * import register from '.../core/setup/register';
 * register(({ view }) => view('App', { template: `...`, ... }));
 * //                                 ^^^^^^^^^^^^^^^^^^^^^^^^
 *```
 */
export interface ViewDefinition<TData = unknown> {
	template:string;
	registrations?:ViewRegistrations|ViewRegistrationMap;
	getData?():TData;
	init?( this: View<TData> ):void;
	update?( this:View<TData>, name?:string ):void;
}


/**
 * This is one of the three [[ViewRegistration]] forms. This allows you to specify how a `View`
 * class should be instantiated.
 */
export interface ViewFactory {
	( key:unknown ):View;
}


/**
 * This type represents view registrations. These can be passed e.g. to:
 *
 *  * [["core/setup/register".RegistrationsBuilder.view]]
 *  * [[ViewRegistrationMap.set]]
 *  * or be stored as the values in a [[ViewRegistrations]] object.
 */
export type ViewRegistration<TData = unknown> = ViewConstructor|ViewDefinition<TData>|ViewFactory;

export interface ViewRegistrations {
	[ name:string ]:ViewRegistration;
}

export class ViewRegistrationMap {

	public static union( others:(ViewRegistrations|ViewRegistrationMap)[] ):ViewRegistrationMap {
		const union = new ViewRegistrationMap();
		for( const other of others ) {
			union.merge( other );
		}
		return union;
	}

	private readonly _map: Map<unknown, ViewRegistration>;

	public constructor() {
		this._map = new Map<unknown, ViewRegistration>();
	}

	public forEach( callback:( registration:ViewRegistration, key:unknown ) => void ):void {
		this._map.forEach( callback );
	}

	public get( key:unknown ):ViewRegistration {
		// also search if there is a constructor registration
		return this._map.get( key ) || (typeof key === 'object' && this._map.get( key.constructor ) );
	}

	public has( key:unknown ):boolean {
		return this._map.get( key ) != undefined;
	}

	public merge( registrations:ViewRegistrations|ViewRegistrationMap ):ViewRegistrationMap {
		if( registrations instanceof ViewRegistrationMap ) {
			registrations.forEach( ( registration, key ) => this.set( key, registration ) );
		}
		else {
			for( const key in registrations ) {
				this.set( key, registrations[ key ] );
			}
		}
		return this;
	}

	public set( key:unknown, registration:ViewRegistration ):ViewRegistrationMap {
		this._map.set( key, registration );
		return this;
	}
}

/** @ignore */
function convertToViewFactory( registration:ViewRegistration ):ViewFactory {

	if( isViewDefinition( registration ) ) {
		const viewClass = class extends View {};
		if( registration.init ) {
			viewClass.prototype['init'] = registration.init;
		}
		if( registration.update ) {
			viewClass.prototype['update'] = registration.update;
		}
		return () => new viewClass({ ...registration, data:registration.getData && registration.getData() });
	}
	else if( isViewConstructor( registration ) ) {
		return () => new registration({});
	}
	else {
		return registration;
	}

	function isViewDefinition( registration:ViewRegistration ):registration is ViewDefinition {
		return typeof registration === 'object';
	}

	function isViewConstructor( registration:ViewConstructor|ViewFactory ):registration is ViewConstructor {
		for ( let proto = Object.getPrototypeOf( registration ); proto; proto = Object.getPrototypeOf( proto ) ) {
			if( proto === View ) {
				return true;
			}
		}
		return false;
	}
}


/** @ignore */
const registrations = new ViewRegistrationMap();


/**
 * @deprecated  Use local view registrations instead, i.e. pass them either to a `View` constructor, or to the `scan` function.
 * @ignore
 */
export const ioc = new class {

	/** Deprecated. Use `ioc[name] = factory` instead.
	 * @deprecated
	 */
	public add( name:string, factory:ViewFactory ):void {
		registrations.set( name, factory );
	}

	/** Deprecated. Use `ioc[name]` instead.
	 * @deprecated
	*/
	public has( name:string ):boolean {
		return registrations.has( name );
	}

	/** Deprecated. Use `name in ioc` instead.
	 * @deprecated
	*/
	public get( name:string ):ViewRegistration {
		return registrations.get( name );
	}
};


/**
 * Registers the `View` class specified by `constructor` under the given `key`
 * such that it can be instantiated in templates using e.g. `<div view="[[key]]"></div>`.
 *
 * ### Example:
 *
 * @example
```typescript
  class Foo extends View { ... }
  register('Foo', Foo);
```
 *
 * Once thus registered, you will be able to instantiate `Foo` in templates e.g. as follows:
 *
```html
  <div view="Foo"></div>
```
 *
 * @param key The key (e.g. a name) under which the ad-hoc `View` class should be registered.
 * @param constructorOrFactory The `View` class, or a parameterless closure (of type `ViewFactory`) that creates a `View` instance, to associate with `key`.
 *
 * @deprecated Use local view registrations instead, i.e. pass them either to a `View` constructor, or to the `scan` function.
 * @ignore
 */
export function register( key:unknown, constructorOrFactory:ViewConstructor|ViewFactory );

/**
 * Registers an ad-hoc `View` class created from the specified `definition` under the given `key`
 * such that it can be instantiated in templates using e.g. `<div view="[[key]]"></div>`.
 *
 * For simple views, this overload can spare you the work of writing a full-blown class.
 *
 * ### Example:
 *
 * @example
```typescript
  register('Foo', {
      template: `<div>Greetings, [[name]].</div>`,
      data: {
          name: 'stranger'
      }
  });
```
 *
 * Once thus registered, you will be able to instantiate `Foo` in templates e.g. as follows:
 *
```html
  <div view="Foo"></div>
```
 *
 * ### Remarks:
 *
 * Note that data binding will not work for the above component because `data` is a plain object
 * whose properties will not dispatch `change` signals. If you want data binding to work,
 * set `data` to an instance of `Model` instead of a plain object.
 *
 * @param key The key (e.g. a name) under which the ad-hoc `View` class should be registered.
 * @param definition Definition of the ad-hoc `View` class. You must specify at least its `template`.
 *
 * @deprecated Use local view registrations instead, i.e. pass them either to a `View` constructor, or to the `scan` function.
 * @ignore
 */
export function register<TData = unknown>( key:unknown, definition:ViewDefinition<TData> );

/**
 * Registers the specified view registrations.
 *
 * @param registrations The registrations to register.
 *
 * @deprecated Use local view registrations instead, i.e. pass them either to a `View` constructor, or to the `scan` function.
 * @ignore
 */
export function register( registrations:ViewRegistrationMap|ViewRegistrations ):void;

export function register<TData = unknown>( keyOrRegistrations:ViewRegistrationMap|ViewRegistrations|unknown, registration?:ViewRegistration<TData> ) {

	if( !registration ) {
		if( keyOrRegistrations instanceof ViewRegistrationMap ) {
			keyOrRegistrations.forEach( ( registration, key ) => {
				register( key, <any>registration );
			});
		}
		else {
			const registrations = <object> keyOrRegistrations;
			for( const key in registrations ) {
				register( key, <any> registrations[ key ] );
			}
		}
	}
	else {
		registrations.set( keyOrRegistrations, registration );
	}
}

export function scan( root:Element, ...registrations:(ViewRegistrations|ViewRegistrationMap)[] ) {
	var temp = new View( { data:setup.data(), registrations: ViewRegistrationMap.union( registrations ) } );
	temp.render( root );
}

export interface ViewConfig<TData = {}> {
	data?:TData,
	registrations?:ViewRegistrations|ViewRegistrationMap,
	template?:string
}

/**
 * A common view class that has a container to render content/template into.
 */
export class View<TData = any> {

	protected _data:TData;
	protected dataSubscription:Subscription;

	public template:Template;
	public parent:View<{}>;
	public readonly registrations:ViewRegistrations|ViewRegistrationMap;

	constructor( config:ViewConfig<TData> ) {

		this.registrations = config.registrations;

		this.template = new Template( config.template );
		this.template.context = {
			data: () => this._data,
			childFactory: this.createChild.bind( this ),
			view: () => this,
		}

		this._data = config.data;
	}


	/**
	 * Renders the view template.
	 * If an empty target is given, the template is rendered into to given target.
	 * If the target is not empty, the content of the target is used as the template.
	 */
	public render( target?:Element ) {

		// do rendering of template
		this.template.render( target );
		this.template.processNode( this.template.root );
		this.template.update();

		// register databinding if model is given
		if( isAttributeChangeObservable( this.data ) ) {
			this.dataSubscription = this.data.change.subscribe( ( change ) => {
				this.update( change.name );
			});
		}

		// everything is ready now we can invoke view's init
		this.init();
	}

	/**
	 * The init function is automatically called after the render function.
	 * So everything is rendered and the node is ready to be used.
	 * Overwrite the init function for custom dom manipulations.
	 */
	protected init() {

	}

	/**
	 * Callback when view is destroyed
	 */
	protected deinit() {

	}

	/**
	 * Returns the root node of the view
	 */
	public get node():Element {
		return this.template.root;
	}

	public destroy() {
		// unsubscript from data
		if( this.dataSubscription ) this.dataSubscription.unsubscribe();
		this.template.destroy();
		this.deinit();
	}

	/**
	 * Updates the view if data attribute has changed
	 */
	public update( name?:string ) {
		this.template.update( name );
	}

	public child<T = View>( name:string ):T {
		return this.template.first( name ) as T;
	}

	public createChild( type:unknown ) {
		var view = convertToViewFactory( this.resolve( type ) )( type );
		view.parent = this;
		return view;
	}

	set data( data:TData ) {
		this._data = data;
	}
	get data():TData {
		return this._data;
	}

	private resolve( type:unknown ):ViewRegistration {
		try {
			// Recursive lookup is done in a separate method (`resolveRecursive`)...
			return this.resolveRecursive( type );
		}
		catch (e) {
			// ... so that we can throw an error that mentions the original `type`
			// instead of always "default" (which is the last thing a recursive
			// lookup would be looking for, but not informative at all):
			renderer.log( 'Registration ', type, ' not found in View', this );
			throw new ViewRegistrationNotFoundError( type );
		}
	}

	private resolveRecursive( type:unknown ):ViewRegistration {
		let local = this.getRegistration( type );
		if( local ) return local;

		// lookup parent view
		if( this.parent ) return this.parent.resolveRecursive( type );

		// lookup global registry
		let global = registrations.get( type );
		if( global ) return global;

		// default lookup
		if( type !== 'default' ) return this.resolveRecursive( 'default' );

		throw new ViewRegistrationNotFoundError( type );
	}


	private getRegistration( key:unknown ):ViewRegistration {

		if( !this.registrations ) return undefined;

		if( this.registrations instanceof ViewRegistrationMap ) {
			return this.registrations.get( key );
		} else {
			// old object lookup case
			return this.registrations[ <string>key ];
		}
	}
}


/** @ignore */
class ViewRegistrationNotFoundError extends Error {

	public constructor( public readonly key:unknown ) {
		super( `There is no view registration for the given key ${JSON.stringify( key )}, nor is there a "default" fallback.` );
	}
}

export default View;
